/**
 * LabUploadsAdmin.tsx - Admin > Integrations > Lab Uploads.
 *
 * Corp super admins add the outside systems allowed to file lab-result PDFs on
 * this practice's patient charts. Each integration has its own key and secret
 * (the secret is shown once, when issued), the lab result type its uploads are
 * filed under, an on/off switch that takes effect on the partner's next
 * upload, and a second switch for whether the same key may also ask how many
 * labs a patient has on file. The Recent uploads tab lists what each partner sent and what
 * happened to it.
 *
 * Modeled on QuestAdmin: PageHeader + Tabs + DataTable + Modal + ConfirmDialog,
 * with the same root wrapper.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Plug, History, Copy, Check } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Tabs } from '@/components/ui/Tabs';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn, DataTableAction } from '@/components/ui/DataTable';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { SwitchField } from '@/components/admin/DialogChrome';
import { Badge } from '@/components/ui/Badge';
import type { BadgeVariant } from '@/types/common.types';
import { getErrorMessage } from '@/services/apiClient';
import { formatCount } from '@/lib/utils';
import {
  listLabUploadIntegrations,
  listLabUploadLabTypes,
  createLabUploadIntegration,
  updateLabUploadIntegration,
  regenerateLabUploadSecret,
  listLabUploadRequests,
  getLabUploadReadiness,
  type LabUploadIntegration,
  type LabUploadCredentials,
  type LabUploadLabType,
  type LabUploadOutcome,
  type LabUploadReadiness,
  type LabUploadRequestRow,
} from '@/services/labUploads.service';

const OUTCOME_LABELS: Record<LabUploadOutcome, { label: string; variant: BadgeVariant }> = {
  filed: { label: 'Filed', variant: 'success' },
  duplicate: { label: 'Already on file', variant: 'neutral' },
  not_matched: { label: 'No matching patient', variant: 'warning' },
  ambiguous: { label: 'More than one matching patient', variant: 'warning' },
  invalid: { label: 'Rejected file', variant: 'error' },
  error: { label: 'Failed', variant: 'error' },
  received: { label: 'In progress', variant: 'info' },
};

const OUTCOME_OPTIONS = (Object.keys(OUTCOME_LABELS) as LabUploadOutcome[])
  .filter((o) => o !== 'received')
  .map((o) => ({ value: o, label: OUTCOME_LABELS[o].label }));

function formatWhen(value: string | null): string {
  if (!value) return 'Never';
  return value.slice(0, 16);
}

const ERROR_BOX = 'text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2';

// ════════════════════════════════════════════════════════════════════════════
// One-time credentials
// ════════════════════════════════════════════════════════════════════════════

function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setCopyFailed(false);
    } catch {
      setCopied(false);
      setCopyFailed(true);
    }
  }

  return (
    <div>
      <div className="text-xs font-medium text-slate-500 mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <code className="flex-1 min-w-0 break-all rounded bg-slate-100 px-2 py-1.5 text-sm text-slate-900">{value}</code>
        <Button variant="ghost" size="sm" onClick={copy} aria-label={`Copy ${label}`}>
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
      {copyFailed && (
        <p className="mt-1 text-xs text-amber-800">Copying didn&apos;t work here. Select the text above and copy it by hand.</p>
      )}
    </div>
  );
}

function CredentialsModal({ credentials, onClose }: { credentials: LabUploadCredentials; onClose: () => void }) {
  return (
    // Only Done closes this: a stray click or Escape would lose a secret that
    // cannot be shown again.
    <Modal
      isOpen
      onClose={onClose}
      title="Send these to the partner"
      size="md"
      closeOnBackdrop={false}
      closeOnEscape={false}
      hideCloseButton
      footer={<Button onClick={onClose}>Done</Button>}
    >
      <div className="space-y-4">
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          This secret won&apos;t be shown again. Copy it now and send it to the partner securely, not by email.
        </p>
        <CopyRow label="Key ID" value={credentials.keyId} />
        <CopyRow label="Secret" value={credentials.secret} />
        <CopyRow label="Practice ID" value={String(credentials.accountId)} />
        <CopyRow label="Upload address" value={credentials.uploadUrl} />
        {credentials.checkUrl && <CopyRow label="Check address" value={credentials.checkUrl} />}
      </div>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Add / edit
// ════════════════════════════════════════════════════════════════════════════

function IntegrationModal({
  integration,
  labTypes,
  onClose,
  onSaved,
}: {
  integration?: LabUploadIntegration | null;
  labTypes: LabUploadLabType[];
  onClose: () => void;
  onSaved: (credentials: LabUploadCredentials | null) => void;
}) {
  const isEdit = integration != null;
  const [name, setName] = useState(integration?.name ?? '');
  const [noteTypeId, setNoteTypeId] = useState<number | null>(integration?.noteTypeId || null);
  const [canCheck, setCanCheck] = useState<boolean>(integration?.canCheck ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const labTypeOptions = labTypes.map((t) => ({ value: t.id, label: t.name }));
  const canSave = name.trim() !== '' && noteTypeId !== null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Enter in a field submits even while the button is disabled.
    if (saving || !canSave || noteTypeId === null) return;

    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        // canCheck only when the admin changed it: the dialog's copy may be
        // stale, and resending it would undo someone else's switch-off.
        await updateLabUploadIntegration(integration.id, {
          name: name.trim(),
          noteTypeId,
          ...(canCheck !== integration.canCheck ? { canCheck } : {}),
        });
        onSaved(null);
      } else {
        const created = await createLabUploadIntegration({ name: name.trim(), noteTypeId, canCheck });
        onSaved(created.credentials);
      }
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, 'The integration could not be saved. Try again.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Edit Lab Upload' : 'Add Lab Upload'}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving || !canSave} loading={saving}>
            {isEdit ? 'Save Changes' : 'Add Lab Upload'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <p className={ERROR_BOX}>{error}</p>}

        <FormField label="Name" required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Who is sending the labs, e.g. NextHealth lab portal"
            maxLength={100}
            required
            autoFocus
          />
        </FormField>

        <FormField label="File labs as" required>
          <Select<number>
            options={labTypeOptions}
            value={noteTypeId}
            onChange={(v) => setNoteTypeId(v)}
            placeholder={labTypeOptions.length ? 'Select a lab result type…' : 'This practice has no lab result types'}
          />
        </FormField>

        <SwitchField id="lab-upload-can-check" label="Can check for labs" checked={canCheck} onChange={setCanCheck} />
      </form>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Readiness
// ════════════════════════════════════════════════════════════════════════════

function ReadinessPanel() {
  const [readiness, setReadiness] = useState<LabUploadReadiness | null>(null);

  useEffect(() => {
    getLabUploadReadiness().then(setReadiness).catch(() => setReadiness(null));
  }, []);

  if (!readiness) return null;

  const items: { ok: boolean; text: string }[] = [
    readiness.secretsConfigured
      ? { ok: true, text: 'Keys can be issued for this practice.' }
      : { ok: false, text: 'Keys can’t be issued yet: this server is missing the secret it shares with the practice servers. Ask the Prospera engineering team to check it.' },
    readiness.labReadingReady
      ? { ok: true, text: 'Lab reading is set up, so uploaded labs will be read into biomarkers.' }
      : { ok: false, text: 'Lab reading isn’t set up for this practice, so uploaded labs will be filed but not read. Set it up under Admin > Integrations > AI.' },
    readiness.ambiguousPatients === 0
      ? { ok: true, text: 'Every patient has a unique name and date of birth.' }
      : {
          ok: false,
          text: `${readiness.ambiguousPatients} patients share a name and date of birth with someone else. Uploads for them are refused unless they include the patient’s ID. Merging the duplicates fixes this.`,
        },
  ];

  return (
    <ul className="mb-4 space-y-1.5 rounded-lg border border-slate-200 bg-white p-4 text-sm">
      {items.map((item) => (
        <li key={item.text} className={item.ok ? 'text-slate-700' : 'text-amber-800'}>
          {item.ok ? '✓ ' : '! '}
          {item.text}
        </li>
      ))}
    </ul>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Integrations tab
// ════════════════════════════════════════════════════════════════════════════

function IntegrationsTab({ showCreate, onCreated }: { showCreate: boolean; onCreated: () => void }) {
  const [rows, setRows] = useState<LabUploadIntegration[]>([]);
  const [labTypes, setLabTypes] = useState<LabUploadLabType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editItem, setEditItem] = useState<LabUploadIntegration | null>(null);
  const [rotateItem, setRotateItem] = useState<LabUploadIntegration | null>(null);
  const [credentials, setCredentials] = useState<LabUploadCredentials | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, types] = await Promise.all([listLabUploadIntegrations(), listLabUploadLabTypes()]);
      setRows(list);
      setLabTypes(types);
    } catch (err) {
      setError(getErrorMessage(err, 'Lab uploads could not be loaded. Refresh the page to try again.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Both switches save one setting and update that row in place. The saved row
  // carries no lastUploadAt, so the row's own is kept.
  async function saveSwitch(row: LabUploadIntegration, change: { active: boolean } | { canCheck: boolean }, failed: string) {
    setError(null);
    try {
      const updated = await updateLabUploadIntegration(row.id, change);
      setRows((prev) => prev.map((r) => (r.id === updated.id ? { ...updated, lastUploadAt: r.lastUploadAt } : r)));
    } catch (err) {
      setError(getErrorMessage(err, failed));
    }
  }

  const toggle = (row: LabUploadIntegration, active: boolean) =>
    saveSwitch(row, { active }, `${row.name} could not be switched ${active ? 'on' : 'off'}. Try again.`);

  const toggleCheck = (row: LabUploadIntegration, canCheck: boolean) =>
    saveSwitch(row, { canCheck }, `Checking for labs could not be switched ${canCheck ? 'on' : 'off'} for ${row.name}. Try again.`);

  async function handleRotate() {
    if (!rotateItem) return;
    setError(null);
    try {
      setCredentials(await regenerateLabUploadSecret(rotateItem.id));
    } catch (err) {
      setError(getErrorMessage(err, 'A new secret could not be issued. The current one still works.'));
    } finally {
      setRotateItem(null);
    }
  }

  const columns: DataTableColumn<LabUploadIntegration>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (row) => (
        <div>
          <div className="font-medium text-slate-900">{row.name}</div>
          <div className="font-mono text-xs text-slate-500">{row.keyId}</div>
        </div>
      ),
    },
    {
      key: 'labTypeName',
      header: 'Filed as',
      render: (row) => <span className="text-slate-800">{row.labTypeName || 'Lab type no longer available'}</span>,
    },
    {
      key: 'active',
      header: 'On',
      render: (row) => (
        <Switch checked={row.active} onChange={(v) => void toggle(row, v)} aria-label={`${row.name} ${row.active ? 'on' : 'off'}`} />
      ),
    },
    {
      key: 'canCheck',
      header: 'Can check for labs',
      render: (row) => (
        <Switch
          checked={row.canCheck}
          onChange={(v) => void toggleCheck(row, v)}
          aria-label={`${row.name} can check for labs: ${row.canCheck ? 'on' : 'off'}`}
        />
      ),
    },
    {
      key: 'lastUploadAt',
      header: 'Last upload',
      render: (row) => <span className="text-slate-700">{formatWhen(row.lastUploadAt)}</span>,
    },
  ];

  const rowActions: DataTableAction<LabUploadIntegration>[] = [
    { label: 'Edit', onClick: (row) => setEditItem(row) },
    { label: 'Issue new secret', onClick: (row) => setRotateItem(row) },
  ];

  return (
    <div className="p-6">
      <ReadinessPanel />
      {error && <p className={`${ERROR_BOX} mb-4`}>{error}</p>}

      <DataTable<LabUploadIntegration>
        data={rows}
        columns={columns}
        keyField="id"
        loading={loading}
        rowActions={rowActions}
        emptyTitle="No lab uploads yet"
        emptyDescription="Add one to let an outside system file lab results on this practice's charts."
      />

      {showCreate && (
        <IntegrationModal labTypes={labTypes} onClose={onCreated} onSaved={(c) => { setCredentials(c); void load(); }} />
      )}
      {editItem && (
        <IntegrationModal integration={editItem} labTypes={labTypes} onClose={() => setEditItem(null)} onSaved={() => void load()} />
      )}
      {credentials && <CredentialsModal credentials={credentials} onClose={() => setCredentials(null)} />}

      <ConfirmDialog
        isOpen={rotateItem !== null}
        title="Issue a new secret"
        message={
          rotateItem
            ? `The current secret for "${rotateItem.name}" stops working immediately. Their uploads will be refused until they switch to the new one.`
            : ''
        }
        confirmLabel="Issue new secret"
        variant="warning"
        onConfirm={handleRotate}
        onClose={() => setRotateItem(null)}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Recent uploads tab
// ════════════════════════════════════════════════════════════════════════════

function RecentUploadsTab() {
  const [rows, setRows] = useState<LabUploadRequestRow[]>([]);
  const [total, setTotal] = useState(0);
  const [outcome, setOutcome] = useState<LabUploadOutcome | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    listLabUploadRequests({ outcome: outcome ?? '' })
      .then((r) => { setRows(r.items); setTotal(r.total); })
      .catch((err) => setError(getErrorMessage(err, 'Recent uploads could not be loaded. Refresh the page to try again.')))
      .finally(() => setLoading(false));
  }, [outcome]);

  const columns: DataTableColumn<LabUploadRequestRow>[] = [
    { key: 'createdAt', header: 'When', render: (row) => <span className="text-slate-700">{formatWhen(row.createdAt)}</span> },
    { key: 'integrationName', header: 'From', render: (row) => <span className="text-slate-800">{row.integrationName || 'Removed integration'}</span> },
    {
      key: 'outcome',
      header: 'Result',
      render: (row) => {
        const o = OUTCOME_LABELS[row.outcome] ?? { label: row.outcome, variant: 'neutral' as BadgeVariant };
        return <Badge variant={o.variant}>{o.label}</Badge>;
      },
    },
    {
      key: 'patientName',
      header: 'Patient',
      render: (row) =>
        row.patientId ? (
          <Link to={`/patients/${row.patientId}`} className="text-blue-600 hover:underline">
            {row.patientName || `Patient #${row.patientId}`}
          </Link>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center gap-3">
        <div className="w-64">
          <Select<LabUploadOutcome>
            options={OUTCOME_OPTIONS}
            value={outcome}
            onChange={(v) => setOutcome(v)}
            placeholder="All results"
            clearable
          />
        </div>
        {!loading && total > rows.length && (
          <span className="text-sm text-slate-500">Showing the latest {formatCount(rows.length)} of {formatCount(total)}</span>
        )}
      </div>
      {error && <p className={`${ERROR_BOX} mb-4`}>{error}</p>}

      <DataTable<LabUploadRequestRow>
        data={rows}
        columns={columns}
        keyField="id"
        loading={loading}
        emptyTitle="No uploads yet"
        emptyDescription="Labs sent by this practice's lab uploads will appear here."
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Page
// ════════════════════════════════════════════════════════════════════════════

export function LabUploadsAdmin() {
  const [activeTab, setActiveTab] = useState('integrations');
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div>
      <div className="px-6 pt-6">
        <PageHeader
          title="Lab Uploads"
          actions={
            activeTab === 'integrations' ? (
              <Button onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Add Lab Upload
              </Button>
            ) : undefined
          }
        />
      </div>

      <Tabs
        tabs={[
          {
            id: 'integrations',
            label: 'Integrations',
            icon: <Plug className="h-4 w-4" />,
            content: <IntegrationsTab showCreate={showCreate && activeTab === 'integrations'} onCreated={() => setShowCreate(false)} />,
          },
          { id: 'recent', label: 'Recent uploads', icon: <History className="h-4 w-4" />, content: <RecentUploadsTab /> },
        ]}
        onChange={(id) => { setActiveTab(id); setShowCreate(false); }}
      />
    </div>
  );
}
